import React, { ChangeEvent, useState } from 'react'
import kebabCase from 'lodash.kebabcase'
import { IntegrationItem } from '@/components/molecules/IntegrationItem/IntegrationItem'
import { CustomLink } from '@/components/atoms/CustomLink/CustomLink'
import { IntegrationsGridProps } from '@/components/organisms/IntegrationsGrid/types'
import { IntegrationItemProps } from '@/components/molecules/IntegrationItem/types'
import { CategoriesMenu, MobileCategoriesMenu } from '@/components/organisms/IntegrationsGrid/components/CategoriesMenu'
import { SearchBox } from '@/components/organisms/IntegrationsGrid/components/SearchBox'
import { Button } from '@/components/atoms/Button/Button'

export function IntegrationsGrid({
  items,
  all_integrations_label,
  select_category_label,
  search_input_placeholder,
  no_results_text,
  no_results_button_text,
  custom_css_class
}: IntegrationsGridProps) {
  const [activeCategory, setActiveCategory] = useState<string | undefined>(all_integrations_label)
  const [searchQuery, setSearchQuery] = useState('')

  const categories = items.flatMap((item) => item.category)
  const categoriesMap = [...new Set(categories)]
  const filterItemsByCategory = (elements: IntegrationItemProps[]) => {
    let filteredItems = elements

    // Apply search query
    if (searchQuery.length > 0) {
      filteredItems = filteredItems.filter((item) => item.name?.toLowerCase().includes(searchQuery.toLowerCase()))
    }

    // Apply category filter
    if (activeCategory !== all_integrations_label) {
      if (activeCategory != null) {
        filteredItems = filteredItems.filter((item) => item.category?.includes(activeCategory))
      }
    }

    return filteredItems
  }

  const filteredItems = filterItemsByCategory(items)

  const handleClearSearch = () => {
    setSearchQuery('')
  }

  const handleSearch = (query: ChangeEvent<HTMLInputElement>) => {
    const searchTerm = query.target.value
    setSearchQuery(searchTerm)
    if (searchTerm.length > 0) {
      setActiveCategory(all_integrations_label)
    }
  }

  const handleSelectCategory = (item: string | undefined) => {
    setActiveCategory(item)
    handleClearSearch()
  }

  const handleOpenChat = () => {
    handleClearSearch()
    // @ts-ignore
    window?.Intercom('showNewMessage')
  }

  return (
    <section className="max-w-screen-desktop mx-auto px-24 netbook:px-90 py-32 netbook:pb-32 netbook:pt-32">
      <SearchBox
        searchQuery={searchQuery}
        handleSearch={handleSearch}
        handleClearSearch={handleClearSearch}
        search_input_placeholder={search_input_placeholder}
      />
      <div className="flex flex-col netbook:flex-row mt-24 netbook:mt-40 gap-16 laptop:gap-24 justify-between">
        <div className="flex flex-col gap-8">
          {searchQuery.length === 0 && (
            <MobileCategoriesMenu
              select_category_label={select_category_label}
              all_integrations_label={all_integrations_label}
              categories={categoriesMap}
              setActiveCategory={handleSelectCategory}
              className="block netbook:hidden mb-8"
              activeCategory={activeCategory}
            />
          )}
          <CategoriesMenu
            all_integrations_label={all_integrations_label}
            activeCategory={activeCategory}
            categories={categoriesMap}
            setActiveCategory={handleSelectCategory}
            className="hidden netbook:block"
          />
        </div>
        {filteredItems.length > 0 ? (
          <div className="grid grid-cols-1 tablet:grid-cols-2 lg:grid-cols-3 gap-24 flex-2 h-fit">
            {filteredItems.map((item) => {
              const customId = kebabCase(item.custom_id) || ''
              const customCssClass = kebabCase(custom_css_class) || ''
              return item.link && item.link?.cached_url ? (
                <CustomLink
                  {...item.link}
                  custom_id={customId}
                  key={item._uid}
                  new_tab={item.link.linktype === 'url'}
                  is_external={item.link.linktype === 'url'}
                  link_url={item.link}
                  className={`max-h-[230px] ${customCssClass}`}
                >
                  <IntegrationItem name={item.name} logo={item.logo} description={item.description} />
                </CustomLink>
              ) : (
                <IntegrationItem
                  key={item._uid}
                  name={item.name}
                  logo={item.logo}
                  description={item.description}
                  custom_css_class={customCssClass}
                  custom_id={customId}
                />
              )
            })}
          </div>
        ) : (
          <div className="flex flex-col gap-16 m-auto">
            <h2 className="font-poppins text-headingS-mobile font-bold text-text-dark-900 text-center">
              {no_results_text}
            </h2>
            <Button
              aria-label="No results found - Open chat with our team"
              type="button"
              as_form={false}
              url={{ url: '', cached_url: '', linktype: '', id: '', fieldtype: '' }}
              // @ts-ignore
              onClick={handleOpenChat}
              variant="primary-small"
            >
              {no_results_button_text}
            </Button>
          </div>
        )}
      </div>
    </section>
  )
}
